/**
 * Message Store
 *
 * Manages messages within conversations
 */

import { create } from 'zustand';

export interface Message {
  id: string;
  conversationId: string;
  sender: 'user' | 'bot' | 'system';
  text: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
  status?: 'sent' | 'delivered' | 'failed';
}

export interface MessageStore {
  messages: Map<string, Message[]>;
  isLoading: boolean;
  error: string | null;
  addMessage: (message: Message) => void;
  getMessages: (conversationId: string) => Message[];
  clearMessages: (conversationId: string) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  messages: new Map(),
  isLoading: false,
  error: null,

  addMessage: (message: Message) =>
    set((state) => {
      const conversationMessages = state.messages.get(message.conversationId) || [];
      const newMap = new Map(state.messages);
      newMap.set(message.conversationId, [...conversationMessages, message]);
      return { messages: newMap, error: null };
    }),

  getMessages: (conversationId: string) => {
    const state = get();
    return state.messages.get(conversationId) || [];
  },

  clearMessages: (conversationId: string) =>
    set((state) => {
      const newMap = new Map(state.messages);
      newMap.delete(conversationId);
      return { messages: newMap };
    }),

  updateMessage: (id: string, updates: Partial<Message>) =>
    set((state) => {
      const newMap = new Map(state.messages);
      for (const [key, messages] of newMap.entries()) {
        const updated = messages.map((m) => (m.id === id ? { ...m, ...updates } : m));
        newMap.set(key, updated);
      }
      return { messages: newMap };
    }),

  setLoading: (isLoading: boolean) => set({ isLoading }),

  setError: (error: string | null) => set({ error }),
}));
